import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { TwitterLogo, FacebookLogo, LinkedinLogo, InstagramLogo, GoogleLogo, Plus, Link } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { CHART_TOOLTIP_STYLE } from "../config/uiHelpers";

const SocialLeads = ({ user, logout }) => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSimulate, setShowSimulate] = useState(false);
  const [simForm, setSimForm] = useState({ name: "", email: "", phone: "", platform: "facebook", campaign_name: "", ad_name: "" });

  const fetchData = useCallback(async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/social-leads`),
        axios.get(`${API}/social-leads/stats`)
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const simulateLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/social-leads/capture`, simForm);
      toast.success("Social lead captured!");
      setShowSimulate(false);
      setSimForm({ name: "", email: "", phone: "", platform: "facebook", campaign_name: "", ad_name: "" });
      fetchData();
    } catch (err) { toast.error("Failed to capture lead"); }
  };

  const getPlatformIcon = (platform) => {
    const size = 20;
    if (platform === "twitter") return <TwitterLogo size={size} weight="fill" className="text-[#1DA1F2]" />;
    if (platform === "facebook") return <FacebookLogo size={size} weight="fill" className="text-[#1877F2]" />;
    if (platform === "linkedin") return <LinkedinLogo size={size} weight="fill" className="text-[#0A66C2]" />;
    if (platform === "instagram") return <InstagramLogo size={size} weight="fill" className="text-[#E4405F]" />;
    if (platform === "google_ads") return <GoogleLogo size={size} weight="fill" className="text-[#4285F4]" />;
    return <Link size={size} className="text-slate-500" />;
  };

  const webhookUrl = `${API}/social-leads/capture`;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-4xl sm:text-5xl tracking-tighter leading-none font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}}>Social Media Leads</h1>
            <p className="text-base text-slate-700">Capture leads directly from social media ad campaigns</p>
          </div>
          <button data-testid="simulate-lead-btn" onClick={() => setShowSimulate(!showSimulate)}
            className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-6 py-3 font-medium transition-colors duration-300 flex items-center gap-2">
            <Plus size={18} /> {showSimulate ? 'Cancel' : 'Simulate Lead'}
          </button>
        </div>

        {/* Webhook URL */}
        <div data-testid="webhook-info" className="bg-slate-900 text-white rounded-none p-5 mb-6">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400 mb-2">Webhook URL (Connect to FB/LinkedIn/Google Ads)</p>
          <code className="text-sm text-[#10B981] break-all">{webhookUrl}</code>
          <p className="text-xs text-slate-400 mt-2">POST JSON: {"{"} name, email, phone, platform, campaign_name, ad_name {"}"}</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="bg-white border border-slate-200 rounded-none shadow-sm p-6">
              <div className="text-3xl font-bold text-[#002FA7]" style={{fontFamily: 'Outfit, sans-serif'}}>{stats.total}</div>
              <div className="text-sm text-slate-600">Total Social Leads</div>
            </div>
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-none shadow-sm p-6">
              <h3 className="text-sm font-semibold mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>Leads by Platform</h3>
              <ResponsiveContainer width="100%" height={150}>
                <BarChart data={stats.by_platform} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis type="number" stroke="#475569" />
                  <YAxis type="category" dataKey="platform" stroke="#475569" width={80} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  <Bar dataKey="count" fill="#002FA7" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Simulate Form */}
        {showSimulate && (
          <div data-testid="simulate-form" className="bg-white border border-slate-200 rounded-none p-8 mb-6 shadow-sm">
            <h3 className="text-xl font-semibold mb-4" style={{fontFamily: 'Outfit, sans-serif'}}>Simulate Social Media Lead Capture</h3>
            <form onSubmit={simulateLead} className="grid grid-cols-2 gap-6">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Name</label>
                <input data-testid="sim-name" type="text" value={simForm.name} onChange={e => setSimForm({...simForm, name: e.target.value})}
                  className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Email</label>
                <input data-testid="sim-email" type="email" value={simForm.email} onChange={e => setSimForm({...simForm, email: e.target.value})}
                  className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" required />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Platform</label>
                <select data-testid="sim-platform" value={simForm.platform} onChange={e => setSimForm({...simForm, platform: e.target.value})}
                  className="rounded-none bg-white border border-slate-300 p-3 w-full outline-none">
                  <option value="facebook">Facebook</option>
                  <option value="instagram">Instagram</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="twitter">Twitter</option>
                  <option value="google_ads">Google Ads</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 block mb-2">Campaign Name</label>
                <input data-testid="sim-campaign" type="text" value={simForm.campaign_name} onChange={e => setSimForm({...simForm, campaign_name: e.target.value})}
                  className="rounded-none bg-white border border-slate-300 focus:border-[#002FA7] p-3 w-full outline-none" />
              </div>
              <div className="col-span-2">
                <button type="submit" className="rounded-none bg-[#10B981] text-white hover:bg-[#059669] px-8 py-3 font-medium">Capture Lead</button>
              </div>
            </form>
          </div>
        )}

        {/* Leads Table */}
        {loading ? <div className="flex justify-center py-20"><div className="loading-spinner"></div></div> : (
          <div data-testid="social-leads-table" className="bg-white border border-slate-200 rounded-none shadow-sm overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-y border-slate-200">
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Platform</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Campaign</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Ad</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Contact</th>
                  <th className="text-left p-4 text-xs uppercase tracking-[0.2em] font-bold text-slate-500">Date</th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead.social_lead_id} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="p-4 flex items-center gap-2">{getPlatformIcon(lead.platform)} <span className="capitalize text-sm">{lead.platform?.replace('_', ' ')}</span></td>
                    <td className="p-4 text-sm">{lead.campaign_name || '-'}</td>
                    <td className="p-4 text-sm text-slate-600">{lead.ad_name || '-'}</td>
                    <td className="p-4 text-sm text-[#002FA7] font-medium">{lead.contact_id?.slice(0, 8)}...</td>
                    <td className="p-4 text-xs text-slate-500">{lead.created_at && new Date(lead.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {leads.length === 0 && <div className="text-center py-12 text-slate-500">No social leads captured yet. Use the webhook or simulate a lead above.</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialLeads;
